import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { selectStationFilter, selectUserSession } from "../../slices/userSession/userSessionSlice";
import { serializeDateToRedux, set_time_to_end_of_day, set_time_to_midnight } from "../../util/dateTimeFormatter";
import { getTimeZoneFromSwaStation } from "../../util/timeZoneService";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DateTime } from "luxon";

import { Box, Divider, List, Paper, Tooltip, Typography } from "@mui/material";
import {
  updateDateTypeFilter,
  updateEndDateFilter,
  updateStartDateFilter,
  updateStatusFilters,
  updateTimeOffTypeFilters,
} from "../../slices/adminTimeOfRequest/adminTimeOfRequestSlice";
import {
  updateDateTypeFilter as updateTradeDateTypeFilter,
  updateEndDateFilter as updateTradeEndDateFilter,
  updateStartDateFilter as updateTradeStartDateFilter,
  updateStatusFilters as updateTradeStatusFilter,
  updateTradeTypeFilters,
} from "../../slices/adminTrade/adminTradeSlice";
import { ACQUIRED_SHIFT, SHIFT } from "../../util/constants";
import {
  fetchDashboardTorSnapshotCount,
  fetchDashboardTradeSnapshotCount
} from "../../util/dashboard/dashboardUtils";
import { TOMORROW } from "../../util/dateHelper";
import { findFeatureByKey } from "../../util/featureAccess/featureAccessUtils";
import {
  DEFAULT_ADMIN_TIME_OFF_REQUEST_TYPE_FILTERS,
  DEFAULT_EMPLOYEE_TIME_OFF_REQUEST_STATUS_FILTERS,
} from "../../util/filterTimeOffRequests";
import { DEFAULT_TRADE_ADMIN_TRADE_TYPE_FILTERS } from "../../util/filterTrades";
import DashboardAdminSnapshotCount from "./DashboardAdminSnapshotCount";

const DashboardAdminTwoDaySnapshotCard = (props) => {
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const dispatch = useDispatch();

  const buildTodayCounts = () => {
    const TODAY = DateTime.now().setZone(getTimeZoneFromSwaStation(stationFilter));
    const TODAY_MIDNIGHT = set_time_to_midnight(TODAY);
    const TODAY_EOD = set_time_to_end_of_day(TODAY);
    let todayCounts = [
      {
        key: "timeOffAdministration",
        fetchFunc: fetchDashboardTorSnapshotCount,
        fetchFuncParams: [userSession.eid, stationFilter, SHIFT, TODAY_MIDNIGHT, TODAY_EOD],
        websockets: "torAdminEvents",
        websocketRefreshTimeMS: 5000,
        onClick: () => {
          dispatch(updateDateTypeFilter(SHIFT));
          dispatch(updateStartDateFilter(serializeDateToRedux(TODAY_MIDNIGHT)));
          dispatch(updateEndDateFilter(serializeDateToRedux(TODAY_EOD)));
          dispatch(updateTimeOffTypeFilters(DEFAULT_ADMIN_TIME_OFF_REQUEST_TYPE_FILTERS));
          dispatch(updateStatusFilters(DEFAULT_EMPLOYEE_TIME_OFF_REQUEST_STATUS_FILTERS));
        },
        color: "primary.swaYellow",
        text: "Time off requests",
        tooltip: "Time Off Requests for today awaiting Admin action.",
      },
      {
        key: "tradeAdministration",
        fetchFunc: fetchDashboardTradeSnapshotCount,
        fetchFuncParams: [userSession.eid, stationFilter, ACQUIRED_SHIFT, TODAY_MIDNIGHT, TODAY_EOD],
        websockets: "tradeAdminEvents",
        websocketRefreshTimeMS: 0,
        onClick: () => {
          dispatch(updateTradeDateTypeFilter(ACQUIRED_SHIFT));
          dispatch(updateTradeStartDateFilter(serializeDateToRedux(TODAY_MIDNIGHT)));
          dispatch(updateTradeEndDateFilter(serializeDateToRedux(TODAY_EOD)));
          dispatch(updateTradeTypeFilters(DEFAULT_TRADE_ADMIN_TRADE_TYPE_FILTERS));
          dispatch(updateTradeStatusFilter(DEFAULT_EMPLOYEE_TIME_OFF_REQUEST_STATUS_FILTERS));
        },
        color: "primary.main",
        text: "Trades",
        tooltip: "Trade requests for today awaiting Admin action.",
      },
      // {
      //   key: "tempAssignmentAdministration",
      //   fetchFunc: fetchDashboardTaedSnapshotCount,
      //   fetchFuncParams: [
      //     stationFilter,
      //     TAED_FILTER_DATE_TYPES.ACTIVE_DATE,
      //     TODAY_MIDNIGHT,
      //     TODAY_EOD,
      //     [INCOMING_FILTER],
      //   ],
      //   websockets: "taedAdminEvents",
      //   websocketRefreshTimeMS: 5000,
      //   onClick: () => {
      //     dispatch(updateTaedDateTypeFilter(TAED_FILTER_DATE_TYPES.ACTIVE_DATE));
      //     dispatch(updateTaedStartDateFilter(serializeDateToRedux(TODAY_MIDNIGHT)));
      //     dispatch(updateTaedEndDateFilter(serializeDateToRedux(TODAY_EOD)));
      //     dispatch(updateIncomingOutgoingFilters([INCOMING_FILTER]));
      //     dispatch(updateTaedStatusFilter(DEFAULT_TEMP_ASSIGNMENT_STATUS_FILTERS));
      //   },
      //   color: "secondary.swaGreen",
      //   text: "Incoming Temps",
      //   tooltip: "The number of temp employees in your station today.",
      // },
      // {
      //   key: "tempAssignmentAdministration",
      //   fetchFunc: fetchDashboardTaedSnapshotCount,
      //   fetchFuncParams: [
      //     stationFilter,
      //     TAED_FILTER_DATE_TYPES.ACTIVE_DATE,
      //     TODAY_MIDNIGHT,
      //     TODAY_EOD,
      //     [OUTGOING_FILTER],
      //   ],
      //   websockets: "taedAdminEvents",
      //   websocketRefreshTimeMS: 5000,
      //   onClick: () => {
      //     dispatch(updateTaedDateTypeFilter(TAED_FILTER_DATE_TYPES.ACTIVE_DATE));
      //     dispatch(updateTaedStartDateFilter(serializeDateToRedux(TODAY_MIDNIGHT)));
      //     dispatch(updateTaedEndDateFilter(serializeDateToRedux(TODAY_EOD)));
      //     dispatch(updateIncomingOutgoingFilters([OUTGOING_FILTER]));
      //     dispatch(updateTaedStatusFilter(DEFAULT_TEMP_ASSIGNMENT_STATUS_FILTERS));
      //   },
      //   color: "primary.swaRed",
      //   text: "Outgoing Temps",
      //   tooltip: "The number of employees in your station who are out on a temp assignment today.",
      // },
    ];
    for (const count of todayCounts) {
      const feature = findFeatureByKey(props.userFeatures, count.key);
      count.feature = feature;
    }

    return todayCounts;
  };

  const buildTomorrowCounts = () => {
    const TOMORROW_DT = TOMORROW(stationFilter);
    const TOMORROW_MIDNIGHT = set_time_to_midnight(TOMORROW_DT);
    const TOMORROW_EOD = set_time_to_end_of_day(TOMORROW_DT);
    let tomorrowCounts = [
      {
        key: "timeOffAdministration",
        fetchFunc: fetchDashboardTorSnapshotCount,
        fetchFuncParams: [userSession.eid, stationFilter, SHIFT, TOMORROW_MIDNIGHT, TOMORROW_EOD],
        websockets: "torAdminEvents",
        websocketRefreshTimeMS: 5000,
        onClick: () => {
          dispatch(updateDateTypeFilter(SHIFT));
          dispatch(updateStartDateFilter(serializeDateToRedux(TOMORROW_MIDNIGHT)));
          dispatch(updateEndDateFilter(serializeDateToRedux(TOMORROW_EOD)));
          dispatch(updateTimeOffTypeFilters(DEFAULT_ADMIN_TIME_OFF_REQUEST_TYPE_FILTERS));
          dispatch(updateStatusFilters(DEFAULT_EMPLOYEE_TIME_OFF_REQUEST_STATUS_FILTERS));
        },
        color: "primary.swaYellow",
        text: "Time off requests",
        tooltip: "Time Off Requests for tomorrow awaiting Admin action.",
      },
      {
        key: "tradeAdministration",
        fetchFunc: fetchDashboardTradeSnapshotCount,
        fetchFuncParams: [userSession.eid, stationFilter, ACQUIRED_SHIFT, TOMORROW_MIDNIGHT, TOMORROW_EOD],
        websockets: "tradeAdminEvents",
        websocketRefreshTimeMS: 0,
        onClick: () => {
          dispatch(updateTradeDateTypeFilter(ACQUIRED_SHIFT));
          dispatch(updateTradeStartDateFilter(serializeDateToRedux(TOMORROW_MIDNIGHT)));
          dispatch(updateTradeEndDateFilter(serializeDateToRedux(TOMORROW_EOD)));
          dispatch(updateTradeTypeFilters(DEFAULT_TRADE_ADMIN_TRADE_TYPE_FILTERS));
          dispatch(updateTradeStatusFilter(DEFAULT_EMPLOYEE_TIME_OFF_REQUEST_STATUS_FILTERS));
        },
        color: "primary.main",
        text: "Trades",
        tooltip: "Trade requests for tomorrow awaiting Admin action.",
      },
      // {
      //   key: "tempAssignmentAdministration",
      //   fetchFunc: fetchDashboardTaedSnapshotCount,
      //   fetchFuncParams: [
      //     stationFilter,
      //     TAED_FILTER_DATE_TYPES.ACTIVE_DATE,
      //     TOMORROW_MIDNIGHT,
      //     TOMORROW_EOD,
      //     [INCOMING_FILTER],
      //   ],
      //   websockets: "taedAdminEvents",
      //   websocketRefreshTimeMS: 5000,
      //   onClick: () => {
      //     dispatch(updateTaedDateTypeFilter(TAED_FILTER_DATE_TYPES.ACTIVE_DATE));
      //     dispatch(updateTaedStartDateFilter(serializeDateToRedux(TOMORROW_MIDNIGHT)));
      //     dispatch(updateTaedEndDateFilter(serializeDateToRedux(TOMORROW_EOD)));
      //     dispatch(updateIncomingOutgoingFilters([INCOMING_FILTER]));
      //     dispatch(updateTaedStatusFilter(DEFAULT_TEMP_ASSIGNMENT_STATUS_FILTERS));
      //   },
      //   color: "secondary.swaGreen",
      //   text: "Incoming Temps",
      //   tooltip: "The number of temp employees in your station tomorrow.",
      // },
      // {
      //   key: "tempAssignmentAdministration",
      //   fetchFunc: fetchDashboardTaedSnapshotCount,
      //   fetchFuncParams: [
      //     stationFilter,
      //     TAED_FILTER_DATE_TYPES.ACTIVE_DATE,
      //     TOMORROW_MIDNIGHT,
      //     TOMORROW_EOD,
      //     [OUTGOING_FILTER],
      //   ],
      //   websockets: "taedAdminEvents",
      //   websocketRefreshTimeMS: 5000,
      //   onClick: () => {
      //     dispatch(updateTaedDateTypeFilter(TAED_FILTER_DATE_TYPES.ACTIVE_DATE));
      //     dispatch(updateTaedStartDateFilter(serializeDateToRedux(TOMORROW_MIDNIGHT)));
      //     dispatch(updateTaedEndDateFilter(serializeDateToRedux(TOMORROW_EOD)));
      //     dispatch(updateIncomingOutgoingFilters([OUTGOING_FILTER]));
      //     dispatch(updateTaedStatusFilter(DEFAULT_TEMP_ASSIGNMENT_STATUS_FILTERS));
      //   },
      //   color: "primary.swaRed",
      //   text: "Outgoing Temps",
      //   tooltip: "The number of employees in your station who are out on a temp assignment tomorrow.",
      // },
    ];
    for (const count of tomorrowCounts) {
      const feature = findFeatureByKey(props.userFeatures, count.key);
      count.feature = feature;
    }

    return tomorrowCounts;
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        backgroundColor: "secondary.swaGray1",
        borderRadius: "5px",
        display: "flex",
        flexDirection: "column",
        maxWidth: "350px",
        justifySelf: "start",
      }}
    >
      <Box
        sx={{
          textAlign: "left",
          marginLeft: "1.5rem",
          marginTop: "1rem",
          marginRight: "1.5rem",
          marginBottom: ".5rem",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Two Day Snapshot{" "}
          <Tooltip
            title="Hover over the various counts for more information. Click on the count to be taken to the respective page."
            placement="top"
          >
            <InfoOutlinedIcon
              sx={{ color: "secondary.swaGray5", verticalAlign: "middle", marginLeft: "5px", marginBottom: "5px" }}
            />
          </Tooltip>
        </Typography>
        <Divider />
      </Box>
      <Typography fontWeight="bold" sx={{ fontSize: "15px", textAlign: "left", marginLeft: "30px" }}>
        TODAY
      </Typography>
      <List>
        {buildTodayCounts().map(
          (item, index) =>
            item.feature && (
              <DashboardAdminSnapshotCount
                {...item}
                key={`${item.key}-${index}`}
                workgroupFilters={props.workgroupFilters}
              />
            )
        )}
      </List>
      <Divider sx={{ marginLeft: "1.5rem", marginRight: "1.5rem" }} />
      <Typography fontWeight="bold" sx={{ fontSize: "15px", marginTop: "10px", textAlign: "left", marginLeft: "30px" }}>
        TOMORROW
      </Typography>
      <List>
        {buildTomorrowCounts().map(
          (item, index) =>
            item.feature && (
              <DashboardAdminSnapshotCount
                {...item}
                key={`${item.key}-${index}`}
                workgroupFilters={props.workgroupFilters}
              />
            )
        )}
      </List>
    </Paper>
  );
};

DashboardAdminTwoDaySnapshotCard.propTypes = {
  workgroupFilters: PropTypes.array.isRequired,
  userFeatures: PropTypes.array.isRequired,
};

export default DashboardAdminTwoDaySnapshotCard;
